import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Upload,
  Card,
  Button,
  Space,
  Typography,
  Tag,
  Select,
  message,
  Collapse,
  Descriptions,
  Spin,
  Popconfirm,
  Alert,
  Row,
  Col,
  Empty,
} from "antd";
import {
  UploadOutlined,
  FileTextOutlined,
  ReloadOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  InfoCircleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import {
  uploadPolicyDocument,
  fetchPolicyDocuments,
  fetchSingleDocument,
  confirmPolicyType,
  reparsePolicyDocument,
  deletePolicyDocument,
  createChecklist,
} from "../../../../Slices/policyCheckSlice";

const { Text, Title } = Typography;
const { Dragger } = Upload;

const POLICY_TYPE_OPTIONS = [
  { value: "AUTO", label: "Auto Policy" },
  { value: "GL", label: "General Liability" },
  { value: "BOP", label: "Business Owners Policy" },
  { value: "CPKG", label: "Commercial Package" },
  { value: "WC", label: "Workers Compensation" },
  { value: "UMB", label: "Umbrella / Excess" },
  { value: "PROP", label: "Property" },
  { value: "CRIM", label: "Crime" },
  { value: "OTHER", label: "Other" },
];

const STATUS_CONFIG = {
  pending: { color: "default", icon: <LoadingOutlined />, text: "Pending" },
  processing: { color: "processing", icon: <LoadingOutlined spin />, text: "AI Parsing..." },
  completed: { color: "success", icon: <CheckCircleOutlined />, text: "Parsed" },
  failed: { color: "error", icon: <CloseCircleOutlined />, text: "Failed" },
};

const PolicyDocumentUpload = ({ requestId, RequestInfo }) => {
  const dispatch = useDispatch();
  const { documents, uploadStatus, activeDocument } = useSelector(
    (state) => state.policyCheck
  );
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [pollingIds, setPollingIds] = useState(new Set());

  // Poll for parsing status on processing documents
  useEffect(() => {
    const processing = documents.filter((d) => d.parsingStatus === "processing");
    if (processing.length === 0) return;

    const interval = setInterval(() => {
      processing.forEach((doc) => {
        dispatch(fetchSingleDocument(doc._id));
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [documents, dispatch]);

  const handleUpload = async (file) => {
    if (file.type !== "application/pdf") {
      message.error("Only PDF files are accepted");
      return false;
    }

    try {
      await dispatch(
        uploadPolicyDocument({ file, requestId })
      ).unwrap();
      message.success("Document uploaded — AI parsing started");
    } catch (err) {
      message.error(err || "Upload failed");
    }
    return false; // prevent default upload
  };

  const handleConfirmType = async (docId, type) => {
    try {
      await dispatch(confirmPolicyType({ documentId: docId, policyType: type })).unwrap();
      message.success(`Policy type confirmed: ${type}`);
    } catch (err) {
      message.error(err || "Failed to confirm type");
    }
  };

  const handleReparse = async (docId) => {
    try {
      await dispatch(reparsePolicyDocument(docId)).unwrap();
      message.info("Re-parsing started...");
    } catch (err) {
      message.error(err || "Failed to reparse");
    }
  };

  const handleDelete = async (docId) => {
    try {
      await dispatch(deletePolicyDocument(docId)).unwrap();
      message.success("Document deleted");
    } catch (err) {
      message.error(err || "Failed to delete");
    }
  };

  const handleCreateChecklist = async (doc) => {
    try {
      await dispatch(
        createChecklist({
          requestId,
          policyType: doc.policyType,
          documentId: doc._id,
          insuredName: doc.parsedData?.insuredName || RequestInfo?.insuredName,
          client: doc.parsedData?.carrier,
        })
      ).unwrap();
      message.success("Checklist created with AI-extracted data! Switch to the Checklist tab.");
    } catch (err) {
      message.error(err || "Failed to create checklist");
    }
  };

  const renderParsedDataPreview = (doc) => {
    if (!doc.parsedData) return <Empty description="No parsed data yet" />;

    const pd = doc.parsedData;
    const sections = [];

    // Common Declarations
    if (pd.insuredName || pd.policyNumber || pd.carrier) {
      sections.push(
        <Descriptions
          key="declarations"
          title="Common Declarations"
          bordered
          size="small"
          column={2}
          style={{ marginBottom: 16 }}
        >
          {pd.insuredName && <Descriptions.Item label="Named Insured">{pd.insuredName}</Descriptions.Item>}
          {pd.mailingAddress && <Descriptions.Item label="Mailing Address">{pd.mailingAddress}</Descriptions.Item>}
          {pd.policyNumber && <Descriptions.Item label="Policy Number">{pd.policyNumber}</Descriptions.Item>}
          {pd.effectiveDate && <Descriptions.Item label="Effective Date">{pd.effectiveDate}</Descriptions.Item>}
          {pd.expirationDate && <Descriptions.Item label="Expiration Date">{pd.expirationDate}</Descriptions.Item>}
          {pd.premium && <Descriptions.Item label="Premium">${Number(pd.premium).toLocaleString()}</Descriptions.Item>}
          {pd.carrier && <Descriptions.Item label="Carrier">{pd.carrier}</Descriptions.Item>}
        </Descriptions>
      );
    }

    // Coverage sections — show non-null ones
    const coverageSections = [
      { key: "generalLiability", title: "General Liability" },
      { key: "property", title: "Property" },
      { key: "auto", title: "Auto" },
      { key: "workersComp", title: "Workers Compensation" },
      { key: "umbrellaExcess", title: "Umbrella/Excess" },
      { key: "crime", title: "Crime" },
      { key: "garage", title: "Garage & Garage Keepers" },
      { key: "inlandMarine", title: "Inland Marine" },
      { key: "epli", title: "EPLI" },
      { key: "directorsOfficers", title: "Directors & Officers" },
      { key: "liquorLiability", title: "Liquor Liability" },
      { key: "motorCarrier", title: "Motor Carrier/Truckers" },
      { key: "employeeBenefits", title: "Employee Benefits" },
    ];

    for (const sec of coverageSections) {
      const data = pd[sec.key];
      if (!data || typeof data !== "object") continue;
      const entries = Object.entries(data).filter(([, v]) => v && v !== "null");
      if (entries.length === 0) continue;

      sections.push(
        <Descriptions
          key={sec.key}
          title={sec.title}
          bordered
          size="small"
          column={2}
          style={{ marginBottom: 16 }}
        >
          {entries.map(([key, val]) => (
            <Descriptions.Item key={key} label={formatFieldName(key)}>
              {val}
            </Descriptions.Item>
          ))}
        </Descriptions>
      );
    }

    // Additional findings from AI
    if (pd.additionalFindings && pd.additionalFindings.length > 0) {
      sections.push(
        <div key="findings" style={{ marginBottom: 16 }}>
          <Title level={5}>AI Findings</Title>
          {pd.additionalFindings.map((finding, i) => (
            <Alert
              key={i}
              type={finding.type === "critical" ? "error" : finding.type === "warning" ? "warning" : "info"}
              message={finding.message}
              showIcon
              style={{ marginBottom: 8 }}
            />
          ))}
        </div>
      );
    }

    return sections.length > 0 ? sections : <Empty description="No data extracted" />;
  };

  return (
    <div>
      {/* Upload Area */}
      <Card style={{ marginBottom: 16 }}>
        <Dragger
          accept=".pdf"
          showUploadList={false}
          beforeUpload={handleUpload}
          disabled={uploadStatus === "uploading"}
        >
          <p className="ant-upload-drag-icon">
            {uploadStatus === "uploading" ? <LoadingOutlined /> : <UploadOutlined />}
          </p>
          <p className="ant-upload-text">
            {uploadStatus === "uploading"
              ? "Uploading & starting AI analysis..."
              : "Click or drag policy PDF here to upload"}
          </p>
          <p className="ant-upload-hint">
            Supports any carrier format — AI will auto-detect the policy type and extract all data
          </p>
        </Dragger>
      </Card>

      {/* Document List */}
      {documents.length === 0 ? (
        <Empty description="No policy documents uploaded yet" />
      ) : (
        <div>
          {documents.map((doc) => {
            const statusConf = STATUS_CONFIG[doc.parsingStatus] || STATUS_CONFIG.pending;
            const isExpanded = expandedDoc === doc._id;

            return (
              <Card
                key={doc._id}
                size="small"
                style={{ marginBottom: 12 }}
                title={
                  <Space>
                    <FileTextOutlined />
                    <Text strong>{doc.fileName}</Text>
                    <Tag icon={statusConf.icon} color={statusConf.color}>
                      {statusConf.text}
                    </Tag>
                    {doc.policyType && (
                      <Tag color="blue">{doc.policyType}</Tag>
                    )}
                  </Space>
                }
                extra={
                  <Space>
                    {doc.parsingStatus === "completed" && (
                      <>
                        <Button
                          size="small"
                          icon={<EyeOutlined />}
                          onClick={() => setExpandedDoc(isExpanded ? null : doc._id)}
                        >
                          {isExpanded ? "Hide" : "View"} Data
                        </Button>
                        <Button
                          size="small"
                          type="primary"
                          onClick={() => handleCreateChecklist(doc)}
                        >
                          Create Checklist
                        </Button>
                      </>
                    )}
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      onClick={() => handleReparse(doc._id)}
                      disabled={doc.parsingStatus === "processing"}
                    >
                      Re-parse
                    </Button>
                    <Popconfirm
                      title="Delete this document?"
                      onConfirm={() => handleDelete(doc._id)}
                    >
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                }
              >
                {/* Policy type confirmation */}
                {doc.parsingStatus === "completed" && !doc.policyTypeConfirmed && (
                  <Alert
                    type="info"
                    showIcon
                    icon={<InfoCircleOutlined />}
                    message={
                      <Space>
                        <Text>AI detected policy type: <strong>{doc.policyType}</strong>. Confirm or change:</Text>
                        <Select
                          size="small"
                          defaultValue={doc.policyType}
                          style={{ width: 200 }}
                          options={POLICY_TYPE_OPTIONS}
                          onChange={(val) => handleConfirmType(doc._id, val)}
                        />
                      </Space>
                    }
                    style={{ marginBottom: 12 }}
                  />
                )}

                {/* Parsing error */}
                {doc.parsingStatus === "failed" && doc.parsingError && (
                  <Alert
                    type="error"
                    message="Parsing Failed"
                    description={doc.parsingError}
                    style={{ marginBottom: 12 }}
                  />
                )}

                {/* Processing spinner */}
                {doc.parsingStatus === "processing" && (
                  <div style={{ textAlign: "center", padding: 24 }}>
                    <Spin tip="AI is reading and extracting data from your policy document..." />
                  </div>
                )}

                {/* Expanded parsed data */}
                {isExpanded && doc.parsingStatus === "completed" && renderParsedDataPreview(doc)}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Convert camelCase fieldKey to human-readable
function formatFieldName(key) {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .replace(/Um Uim/g, "UM/UIM")
    .replace(/Pip/g, "PIP")
    .replace(/Bpp/g, "BPP")
    .replace(/Bi Ee/g, "BI/EE")
    .replace(/Gl /g, "GL ")
    .replace(/El /g, "EL ")
    .replace(/Fein/g, "FEIN")
    .replace(/Edp/g, "EDP")
    .replace(/Mcs90/g, "MCS90")
    .replace(/Epli/g, "EPLI")
    .replace(/Uiie/g, "UIIE");
}

export default PolicyDocumentUpload;
